import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import { getStoredData, saveStoredData, getSettings } from '../utils/storage';
import { format, isWithinInterval, startOfDay, endOfDay, parseISO } from 'date-fns';
import { generatePDF } from '../utils/pdfGenerator';

const { FiEdit2, FiTrash2, FiFileText, FiSearch, FiDownload, FiPlus, FiCalendar, FiFilter, FiXCircle, FiTrendingUp } = FiIcons;

export default function Dashboard() {
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const settings = getSettings();

  useEffect(() => {
    setData(getStoredData().sort((a, b) => new Date(b.date) - new Date(a.date)));
  }, []);

  const formatCurrency = (amount) => {
    return Number(amount).toFixed(2).replace('.', ',') + ' €';
  };

  const handleDelete = (id) => {
    if (window.confirm('Möchten Sie diese Gutschrift wirklich löschen?')) {
      const newData = data.filter(item => item.id !== id);
      saveStoredData(newData);
      setData(newData);
    }
  };

  const handleDownload = async (item) => {
    await generatePDF(item, settings);
  };

  const calculateBrutto = (items) => {
    const netto = items.reduce((sum, i) => sum + (Number(i.quantity) * Number(i.price)), 0);
    return netto * 1.19;
  };

  // Filter-Logik
  const filteredData = data.filter(item => {
    const matchesSearch = item.number.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         item.recipientName.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesDate = true;
    const itemDate = parseISO(item.date);
    
    if (startDate && endDate) {
      matchesDate = isWithinInterval(itemDate, { 
        start: startOfDay(parseISO(startDate)), 
        end: endOfDay(parseISO(endDate)) 
      });
    } else if (startDate) {
      matchesDate = itemDate >= startOfDay(parseISO(startDate));
    } else if (endDate) {
      matchesDate = itemDate <= endOfDay(parseISO(endDate));
    }

    return matchesSearch && matchesDate;
  });

  // Berechnungen für Stats
  const totalSum = filteredData.reduce((sum, item) => sum + calculateBrutto(item.items), 0);

  const resetFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">Dashboard</h2>
          <p className="text-slate-500">Verwalten Sie Ihre Stempel-Gutschriften</p>
        </div>
        <Link to="/new" className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 flex items-center justify-center gap-2">
          <SafeIcon icon={FiPlus} /> Neue Gutschrift
        </Link>
      </div>

      {/* Filter Bereich */}
      <div className="bg-white p-4 rounded-2xl ring-1 ring-slate-200 shadow-sm space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[280px] relative">
            <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Suche</label>
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input 
                type="text" 
                placeholder="Nummer oder Empfänger..." 
                className="w-full pl-11 pr-4 py-2.5 rounded-xl border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none bg-slate-50/50"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className="w-44">
            <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Von</label>
            <div className="relative">
              <SafeIcon icon={FiCalendar} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input 
                type="date" 
                className="w-full pl-10 pr-3 py-2.5 rounded-xl border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none bg-slate-50/50 text-sm"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
          </div>

          <div className="w-44">
            <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Bis</label>
            <div className="relative">
              <SafeIcon icon={FiCalendar} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input 
                type="date" 
                className="w-full pl-10 pr-3 py-2.5 rounded-xl border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none bg-slate-50/50 text-sm"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          {(searchTerm || startDate || endDate) && (
            <button 
              onClick={resetFilters}
              className="p-2.5 text-slate-400 hover:text-red-500 transition-colors mb-0.5"
              title="Filter zurücksetzen"
            >
              <SafeIcon icon={FiXCircle} className="text-xl" />
            </button>
          )}
        </div>
      </div>

      {/* Statistik Karten */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl ring-1 ring-slate-200 flex items-center justify-between shadow-sm">
          <div>
            <p className="text-xs text-slate-500 uppercase font-bold tracking-wider mb-1">Gutschriften</p>
            <p className="text-3xl font-black text-slate-900">{filteredData.length}</p>
          </div>
          <div className="p-4 bg-blue-50 text-blue-600 rounded-2xl">
            <SafeIcon icon={FiFileText} className="text-2xl" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl ring-1 ring-slate-200 flex items-center justify-between shadow-sm border-l-4 border-l-emerald-500">
          <div>
            <p className="text-xs text-slate-500 uppercase font-bold tracking-wider mb-1">Gesamtumsatz (Brutto)</p>
            <p className="text-3xl font-black text-slate-900">{formatCurrency(totalSum)}</p>
          </div>
          <div className="p-4 bg-emerald-50 text-emerald-600 rounded-2xl">
            <SafeIcon icon={FiTrendingUp} className="text-2xl" />
          </div>
        </div>
      </div>

      {/* Tabelle */}
      <div className="bg-white rounded-2xl ring-1 ring-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="px-6 py-4 text-sm font-semibold text-slate-600">Nummer</th>
                <th className="px-6 py-4 text-sm font-semibold text-slate-600">Empfänger</th>
                <th className="px-6 py-4 text-sm font-semibold text-slate-600">Datum</th>
                <th className="px-6 py-4 text-sm font-semibold text-slate-600 text-right">Brutto-Betrag</th>
                <th className="px-6 py-4 text-sm font-semibold text-slate-600 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredData.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-4 font-mono font-bold text-blue-600">{item.number}</td>
                  <td className="px-6 py-4 font-medium text-slate-900">{item.recipientName}</td>
                  <td className="px-6 py-4 text-slate-500">{format(new Date(item.date), 'dd.MM.yyyy')}</td>
                  <td className="px-6 py-4 font-semibold text-right">
                    {formatCurrency(calculateBrutto(item.items))}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button 
                        onClick={() => handleDownload(item)} 
                        className="p-2 text-slate-400 hover:text-blue-600 transition-colors"
                        title="PDF Speichern unter..."
                      >
                        <SafeIcon icon={FiDownload} />
                      </button>
                      <Link to={`/edit/${item.id}`} className="p-2 text-slate-400 hover:text-amber-600 transition-colors">
                        <SafeIcon icon={FiEdit2} />
                      </Link>
                      <button 
                        onClick={() => handleDelete(item.id)} 
                        className="p-2 text-slate-400 hover:text-red-600 transition-colors"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredData.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-400 italic">
                    Keine Einträge für den gewählten Zeitraum oder Suchbegriff gefunden.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}